/**
 * /carpe-diem — F-030 follow-up.
 *
 * Read-only Carpe Diem matrix for the client web. Renders the active
 * protocol from the API + the catalog (for chip names + affirmations).
 * Three views (#1): **Focus** = the base matrix (identity + virtues per axis);
 * **Full** = + the per-axis Actions (behaviours tagged to virtues); **Pool** =
 * Full + the full-width Activity Pool (every tracked activity, with its virtue
 * bindings). Each view is a strict superset of the previous one.
 *
 * Client can edit their own affirmations inline; coach edits live in
 * the macOS app.
 */

import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/app/auth-context';
import {
  ApiError,
  getCarpeDiemCatalog,
  getCarpeDiemProtocol,
  getClientSelf,
  listTrackingItems,
  setCarpeDiemAffirmation,
  setTrackingItemVirtues,
  updateTrackingItem,
  type CarpeDiemCatalogVirtue,
  type CarpeDiemProtocol,
  type TrackingItem,
  type TrackingItemDayType,
} from '@/lib/api-client';
import { AxisIcon } from '@/components/axis-icon';
import { StepEntryBanner } from '@/components/step-entry-banner';

type Axis = 'energy' | 'work' | 'love';
type Mode = 'focus' | 'full' | 'pool';

const AXES: Axis[] = ['energy', 'work', 'love'];

export function CarpeDiemPage(): JSX.Element {
  const { t } = useTranslation();
  const { accessToken, user } = useAuth();
  const clientId = user?.clientId ?? null;

  const [protocolState, setProtocolState] = useState<CarpeDiemProtocol | null>(null);
  const [catalog, setCatalog] = useState<CarpeDiemCatalogVirtue[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<Mode>('focus');
  /**
   * NL-040 — tracking items (with axes + dayTypes + isKeyBehavior).
   * Fetched alongside the protocol so Full-mode Behaviors can render
   * per-behavior day-type chips at Step 7+.
   */
  const [trackingItems, setTrackingItems] = useState<TrackingItem[]>([]);
  /**
   * NL-040 — client's current step. Day-type config is gated to Step 7+
   * (the Step where day-type categorization becomes a coaching focus).
   */
  const [currentStep, setCurrentStep] = useState<number>(0);

  useEffect(() => {
    if (!clientId) {
      setLoading(false);
      return;
    }
    let active = true;
    void (async () => {
      setLoading(true);
      try {
        const [p, c, items, self] = await Promise.all([
          getCarpeDiemProtocol(accessToken, clientId),
          getCarpeDiemCatalog(accessToken, clientId),
          // NL-040 — best-effort: empty list if endpoint fails (no day-type chips).
          listTrackingItems(accessToken ?? '', clientId).catch(() => []),
          // NL-040 — best-effort: keep currentStep at 0 if it fails (no day-type chips).
          getClientSelf(accessToken ?? '', clientId).catch(() => ({ current_step: 0 })),
        ]);
        if (!active) return;
        setProtocolState(p);
        setCatalog(c);
        setTrackingItems(items);
        setCurrentStep(self.current_step);
      } catch (cause) {
        if (active) {
          setError(
            cause instanceof ApiError ? cause.envelope.message : t('carpeDiem.errors.generic'),
          );
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [accessToken, clientId, t]);

  const catalogById = useMemo(() => {
    const map = new Map<string, CarpeDiemCatalogVirtue>();
    for (const v of catalog) map.set(v.id, v);
    return map;
  }, [catalog]);

  if (!clientId) {
    return (
      <section className="max-w-4xl mx-auto px-s5 py-s8" data-testid="carpe-diem-missing-client">
        <h1 className="font-display text-display-xl text-ink">{t('carpeDiem.title')}</h1>
        <p className="mt-s3 text-flame-ember">{t('carpeDiem.errors.missingClient')}</p>
      </section>
    );
  }

  if (loading) {
    return (
      <section className="max-w-4xl mx-auto px-s5 py-s8" data-testid="carpe-diem-loading">
        <p className="text-gray-2">{t('carpeDiem.loading')}</p>
      </section>
    );
  }

  if (error) {
    return (
      <section className="max-w-4xl mx-auto px-s5 py-s8" data-testid="carpe-diem-error">
        <h1 className="font-display text-display-xl text-ink">{t('carpeDiem.title')}</h1>
        <p className="mt-s3 text-flame-ember" role="alert">
          {error}
        </p>
      </section>
    );
  }

  if (!protocolState) {
    return (
      <section className="max-w-4xl mx-auto px-s5 py-s8" data-testid="carpe-diem-empty">
        <header className="mb-s5">
          <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
            {t('carpeDiem.eyebrow')}
          </p>
          <h1 className="font-display text-display-xl text-ink mt-s2">{t('carpeDiem.title')}</h1>
        </header>
        <article className="rounded-lg border border-bone-3 bg-bone-2 p-s5">
          <p className="text-gray-2">{t('carpeDiem.empty.body')}</p>
        </article>
      </section>
    );
  }

  return (
    <section className="max-w-4xl mx-auto px-s5 py-s7" data-testid="carpe-diem-built">
      {/* NL-010 — first-login banner when Carpe Diem unlocks at Step 2. */}
      <StepEntryBanner stepNumber={2} variantKey="carpeDiemUnlocked" />
      <header className="mb-s5 flex items-start justify-between gap-s4 flex-wrap">
        <div>
          <p className="font-mono text-xs uppercase tracking-wide text-gray-1">
            {t('carpeDiem.eyebrow')}
          </p>
          <h1 className="font-display text-display-xl text-ink mt-s2">{t('carpeDiem.title')}</h1>
        </div>
        <ModeToggle mode={mode} onChange={setMode} t={t} />
      </header>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-s4">
        {(AXES as ('energy' | 'work' | 'love')[]).map((axis) => (
          <AxisColumn
            key={axis}
            axis={axis}
            protocol={protocolState}
            catalogById={catalogById}
            mode={mode}
            accessToken={accessToken}
            clientId={clientId}
            currentStep={currentStep}
            trackingItems={trackingItems}
            onTrackingItemUpdated={(updated) => {
              setTrackingItems((prev) =>
                prev.map((i) => (i.id === updated.id ? updated : i)),
              );
            }}
            onAffirmationSaved={(virtueId, text) => {
              setCatalog((prev) =>
                prev.map((v) => (v.id === virtueId ? { ...v, affirmation: text } : v)),
              );
            }}
            t={t}
          />
        ))}
      </div>

      {/* #1 — Pool view only: the full-width Activity Pool — every tracked
          activity, with an edit dialog to set its multiplier, category
          (AM / Anytime / PM) and the virtue(s) it serves. */}
      {mode === 'pool' && (
        <PoolActivitiesBox
          trackingItems={trackingItems}
          catalog={catalog}
          accessToken={accessToken}
          clientId={clientId}
          onTrackingItemUpdated={(updated) =>
            setTrackingItems((prev) =>
              prev.map((i) => (i.id === updated.id ? updated : i)),
            )
          }
          t={t}
        />
      )}
    </section>
  );
}

interface ModeToggleProps {
  mode: Mode;
  onChange: (m: Mode) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function ModeToggle({ mode, onChange, t }: ModeToggleProps): JSX.Element {
  return (
    <div className="inline-flex rounded-md border border-bone-3 bg-bone-2 p-1 text-sm">
      {(['focus', 'full', 'pool'] as const).map((m) => (
        <button
          key={m}
          type="button"
          onClick={() => onChange(m)}
          className={`px-s3 py-1 rounded-sm font-medium transition-colors ${
            mode === m ? 'bg-flame text-bone-1' : 'text-gray-2 hover:text-ink'
          }`}
          data-testid={`carpe-diem-mode-${m}`}
        >
          {t(`carpeDiem.mode.${m}`)}
        </button>
      ))}
    </div>
  );
}

interface AxisColumnProps {
  axis: Axis;
  protocol: CarpeDiemProtocol;
  catalogById: Map<string, CarpeDiemCatalogVirtue>;
  mode: Mode;
  accessToken: string | null;
  clientId: string;
  /** NL-040 — gates the day-type config section to Step 7+. */
  currentStep: number;
  /** NL-040 — full list of client's tracking items; filtered to this axis below. */
  trackingItems: TrackingItem[];
  /** NL-040 — bubble PATCH result back to the parent for optimistic update. */
  onTrackingItemUpdated: (updated: TrackingItem) => void;
  onAffirmationSaved: (virtueId: string, text: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function AxisColumn({
  axis,
  protocol,
  catalogById,
  mode,
  accessToken,
  clientId,
  currentStep,
  trackingItems,
  onTrackingItemUpdated,
  onAffirmationSaved,
  t,
}: AxisColumnProps): JSX.Element {
  const identityText = protocol.identity[axis];
  const ids = protocol.matrix[axis];

  // NL-040 — behaviours tagged to this axis, non-archived, sorted by label
  // for a stable list order.
  // #6 — an activity belongs to this axis column if it's tagged with the
  // axis directly OR bound to a virtue of that axis. An activity serving
  // more than one axis therefore shows once under each.
  const axisBehaviors = useMemo(
    () =>
      trackingItems
        .filter(
          (i) =>
            !i.archivedAt &&
            ((i.axes ?? []).includes(axis) ||
              (i.virtues ?? []).some((v) => v.axis === axis)),
        )
        .sort((a, b) => a.label.localeCompare(b.label)),
    [trackingItems, axis],
  );
  // Actions/Behaviours appear in Full and Pool (Pool ⊃ Full ⊃ Focus).
  const showActions = mode !== 'focus';
  const showDayTypeConfig = showActions && currentStep >= 7 && axisBehaviors.length > 0;

  return (
    <article className="rounded-lg border border-bone-3 bg-bone-2 p-s4">
      <p className="font-mono text-xs uppercase tracking-wide text-flame-deep mb-s2">
        {t(`carpeDiem.axis.${axis}`)}
      </p>

      {/* Identity */}
      <div className="rounded-md bg-flame-glow/30 border border-flame-deep/30 px-s3 py-s2 mb-s3">
        <p className="font-mono text-[10px] uppercase tracking-wide text-flame-deep mb-s1">
          {t('carpeDiem.row.identity')}
        </p>
        {identityText ? (
          <p className="font-display text-base italic text-ink">{identityText}</p>
        ) : (
          <p className="text-sm italic text-gray-1">{t('carpeDiem.identity.empty')}</p>
        )}
      </div>

      {/* Virtues */}
      <div className="space-y-s2 mb-s3">
        <p className="font-mono text-[10px] uppercase tracking-wide text-gray-1">
          {t('carpeDiem.row.virtues')}
        </p>
        {ids.length === 0 && (
          <p className="text-sm italic text-gray-1">{t('carpeDiem.virtues.empty')}</p>
        )}
        {ids.map((vid, idx) => {
          const v = catalogById.get(vid);
          if (!v) return null;
          return (
            <VirtueCard
              key={vid}
              virtue={v}
              isLead={idx === 0}
              accessToken={accessToken}
              clientId={clientId}
              onAffirmationSaved={onAffirmationSaved}
              t={t}
            />
          );
        })}
      </div>

      {/* Actions/Behaviors (Full + Pool) — #6: a bullet list of the activities
          tied to this axis, so the client can feel where the weight of their
          daily practice sits (and shift priorities). An activity serving more
          than one axis appears once under each. */}
      {showActions && (
        <div className="space-y-s1">
          <p className="font-mono text-[10px] uppercase tracking-wide text-gray-1">
            {t('carpeDiem.row.behaviors')}
          </p>
          {axisBehaviors.length === 0 ? (
            <p className="text-xs italic text-gray-1">{t('carpeDiem.behaviors.empty')}</p>
          ) : (
            <ul className="space-y-[2px]" data-testid={`carpe-diem-behaviors-${axis}`}>
              {axisBehaviors.map((item) => (
                <li
                  key={item.id}
                  className="flex items-baseline gap-s1 text-xs text-gray-2"
                >
                  <span aria-hidden="true" className="text-flame-deep">
                    •
                  </span>
                  <span className="text-ink">{item.label}</span>
                  {item.weight && item.weight > 1 ? (
                    <span className="font-mono text-[10px] text-flame-deep">
                      ×{item.weight}
                    </span>
                  ) : null}
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      {/* NL-040 — per-behavior Day-Type config. Step 7+ only, Full mode. */}
      {showDayTypeConfig && (
        <div className="mt-s3 pt-s3 border-t border-bone-3 space-y-s2">
          <p
            className="font-mono text-[10px] uppercase tracking-wide text-flame-deep"
            data-testid={`carpe-diem-daytype-heading-${axis}`}
          >
            {t('carpeDiem.dayTypes.heading')}
          </p>
          <p className="text-[11px] text-gray-2">{t('carpeDiem.dayTypes.hint')}</p>
          {axisBehaviors.map((item) => (
            <BehaviorDayTypeRow
              key={item.id}
              item={item}
              accessToken={accessToken}
              clientId={clientId}
              onUpdated={onTrackingItemUpdated}
              t={t}
            />
          ))}
        </div>
      )}
    </article>
  );
}

/**
 * NL-040 — single tracking-item row with 2 day-type toggle chips.
 * Optimistic update — toggle visually flips, then PATCH; on error the
 * UI rolls back via the parent's onUpdated source-of-truth.
 */
interface BehaviorDayTypeRowProps {
  item: TrackingItem;
  accessToken: string | null;
  clientId: string;
  onUpdated: (updated: TrackingItem) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function BehaviorDayTypeRow({
  item,
  accessToken,
  clientId,
  onUpdated,
  t,
}: BehaviorDayTypeRowProps): JSX.Element {
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = async (dt: TrackingItemDayType): Promise<void> => {
    setErr(null);
    setSaving(true);
    const current = new Set<TrackingItemDayType>(item.dayTypes);
    if (current.has(dt)) current.delete(dt);
    else current.add(dt);
    const next: TrackingItemDayType[] = Array.from(current);
    try {
      const updated = await updateTrackingItem(accessToken ?? '', clientId, item.id, {
        dayTypes: next,
      });
      onUpdated(updated);
    } catch (cause) {
      setErr(
        cause instanceof ApiError ? cause.envelope.message : t('carpeDiem.errors.generic'),
      );
    } finally {
      setSaving(false);
    }
  };

  const has = (dt: TrackingItemDayType): boolean => item.dayTypes.includes(dt);

  return (
    <div
      className="rounded-md border border-bone-3 bg-bone-1 px-s2 py-s2"
      data-testid={`carpe-diem-daytype-row-${item.id}`}
    >
      <p className="text-xs text-ink truncate">{item.label}</p>
      <div className="mt-s1 flex flex-wrap gap-s1">
        {(['good', 'fallback'] as const).map((dt) => {
          const active = has(dt);
          return (
            <button
              key={dt}
              type="button"
              onClick={() => void toggle(dt)}
              disabled={saving}
              data-testid={`carpe-diem-daytype-${item.id}-${dt}`}
              data-active={active ? 'true' : 'false'}
              className={
                'inline-flex items-center gap-1 rounded-full px-s2 py-[2px] text-[11px] font-medium transition-colors ' +
                (active
                  ? 'bg-flame text-bone-1 border border-flame-deep'
                  : 'bg-bone-2 text-gray-2 border border-bone-3 hover:border-flame-deep/40')
              }
            >
              <span aria-hidden="true">{active ? '●' : '○'}</span>
              {t(`carpeDiem.dayTypes.${dt}`)}
            </button>
          );
        })}
      </div>
      {item.dayTypes.length === 0 ? (
        <p className="mt-s1 text-[10px] text-gray-1 italic">
          {t('carpeDiem.dayTypes.bothHint')}
        </p>
      ) : null}
      {err ? <p className="mt-s1 text-[10px] text-flame-ember">{err}</p> : null}
    </div>
  );
}

interface VirtueCardProps {
  virtue: CarpeDiemCatalogVirtue;
  isLead: boolean;
  accessToken: string | null;
  clientId: string;
  onAffirmationSaved: (virtueId: string, text: string) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function VirtueCard({
  virtue,
  isLead,
  accessToken,
  clientId,
  onAffirmationSaved,
  t,
}: VirtueCardProps): JSX.Element {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(virtue.affirmation ?? '');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  useEffect(() => {
    setDraft(virtue.affirmation ?? '');
  }, [virtue.affirmation]);

  const save = async (): Promise<void> => {
    setErr(null);
    setSaving(true);
    try {
      await setCarpeDiemAffirmation(accessToken ?? '', clientId, virtue.id, draft.trim());
      onAffirmationSaved(virtue.id, draft.trim());
      setEditing(false);
    } catch (cause) {
      setErr(cause instanceof ApiError ? cause.envelope.message : t('carpeDiem.errors.generic'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div
      className={`rounded-md px-s3 py-s2 border ${
        isLead
          ? 'border-flame-deep/50 bg-flame-glow/15'
          : 'border-bone-3 bg-bone-1'
      }`}
    >
      <div className="flex items-center gap-s2">
        {isLead && <span aria-hidden="true">★</span>}
        <p className="font-semibold text-ink">{virtue.name}</p>
      </div>
      {editing ? (
        <div className="mt-s2 flex flex-col gap-s2">
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            maxLength={280}
            rows={2}
            className="input-field text-sm italic"
            data-testid={`carpe-diem-affirmation-edit-${virtue.id}`}
          />
          <div className="flex justify-end gap-s2">
            <button
              type="button"
              className="btn-ghost text-xs"
              onClick={() => {
                setEditing(false);
                setDraft(virtue.affirmation ?? '');
              }}
              disabled={saving}
            >
              {t('common.actions.cancel')}
            </button>
            <button
              type="button"
              className="btn-secondary text-xs"
              onClick={() => {
                void save();
              }}
              disabled={saving}
            >
              {saving ? t('carpeDiem.affirmation.saving') : t('carpeDiem.affirmation.save')}
            </button>
          </div>
          {err && <p className="text-xs text-flame-ember">{err}</p>}
        </div>
      ) : virtue.affirmation && virtue.affirmation.length > 0 ? (
        <button
          type="button"
          className="mt-s1 text-sm italic text-gray-2 text-left w-full hover:text-ink transition-colors"
          onClick={() => setEditing(true)}
          data-testid={`carpe-diem-affirmation-${virtue.id}`}
        >
          “{virtue.affirmation}”
        </button>
      ) : (
        <button
          type="button"
          className="mt-s1 text-xs italic text-gray-1 hover:text-flame-deep transition-colors"
          onClick={() => setEditing(true)}
          data-testid={`carpe-diem-affirmation-empty-${virtue.id}`}
        >
          + {t('carpeDiem.affirmation.add')}
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────  #6 — Activity Pool box  ───────────────────────── */

const POOL_CATEGORIES: { key: TrackingItem['category']; labelKey: string }[] = [
  { key: 'am_bookend', labelKey: 'checkIn.tracker.category.am_bookend' },
  { key: 'anywhere', labelKey: 'checkIn.tracker.category.anywhere' },
  { key: 'pm_bookend', labelKey: 'checkIn.tracker.category.pm_bookend' },
];

interface PoolBoxProps {
  trackingItems: TrackingItem[];
  catalog: CarpeDiemCatalogVirtue[];
  accessToken: string | null;
  clientId: string;
  onTrackingItemUpdated: (updated: TrackingItem) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

/**
 * #6 — full-width pool of every (non-archived) tracked activity, grouped by
 * bookend slot. Each editable activity opens a dialog to set its multiplier,
 * category, and the virtue(s) it serves. System-managed items are read-only.
 */
function PoolActivitiesBox({
  trackingItems,
  catalog,
  accessToken,
  clientId,
  onTrackingItemUpdated,
  t,
}: PoolBoxProps): JSX.Element | null {
  const [editing, setEditing] = useState<TrackingItem | null>(null);
  const pool = useMemo(
    () =>
      trackingItems
        .filter((i) => !i.archivedAt)
        .sort((a, b) => a.label.localeCompare(b.label)),
    [trackingItems],
  );
  if (pool.length === 0) return null;
  return (
    <article
      className="mt-s6 rounded-lg border border-bone-3 bg-bone-2 p-s5"
      data-testid="carpe-diem-pool"
    >
      <header className="mb-s3">
        <p className="font-mono text-xs uppercase tracking-wide text-flame-deep">
          {t('carpeDiem.pool.title')}
        </p>
        <p className="mt-s1 text-sm text-gray-2">{t('carpeDiem.pool.hint')}</p>
      </header>
      <div className="space-y-s4">
        {POOL_CATEGORIES.map(({ key, labelKey }) => {
          const items = pool.filter((i) => i.category === key);
          if (items.length === 0) return null;
          return (
            <div key={key}>
              <p className="font-mono text-[10px] uppercase tracking-wide text-gray-1 mb-s1">
                {t(labelKey)}
              </p>
              <ul className="space-y-s1">
                {items.map((item) => (
                  <PoolRow
                    key={item.id}
                    item={item}
                    onEdit={() => setEditing(item)}
                    t={t}
                  />
                ))}
              </ul>
            </div>
          );
        })}
      </div>
      {editing && (
        <ActivityEditDialog
          item={editing}
          catalog={catalog}
          accessToken={accessToken}
          clientId={clientId}
          onClose={() => setEditing(null)}
          onSaved={(updated) => {
            onTrackingItemUpdated(updated);
            setEditing(null);
          }}
          t={t}
        />
      )}
    </article>
  );
}

function PoolRow({
  item,
  onEdit,
  t,
}: {
  item: TrackingItem;
  onEdit: () => void;
  t: ReturnType<typeof useTranslation>['t'];
}): JSX.Element {
  const isSystem = item.createdBy === 'system';
  return (
    <li className="flex items-center gap-s2 rounded-md border border-bone-3 bg-bone-1 px-s3 py-s2">
      <span className="text-sm text-ink flex-1 truncate">{item.label}</span>
      {(item.axes ?? []).map((ax) => (
        <AxisIcon key={ax} axis={ax} size={13} />
      ))}
      {item.weight && item.weight > 1 ? (
        <span className="font-mono text-[10px] text-flame-deep">×{item.weight}</span>
      ) : null}
      {isSystem ? (
        <span className="text-[11px] italic text-gray-1">
          {t('carpeDiem.pool.coachManaged')}
        </span>
      ) : (
        <button
          type="button"
          onClick={onEdit}
          className="text-xs font-medium text-flame-deep hover:text-flame-ember"
          data-testid={`carpe-diem-pool-edit-${item.id}`}
        >
          {t('carpeDiem.pool.edit')}
        </button>
      )}
    </li>
  );
}

interface ActivityEditDialogProps {
  item: TrackingItem;
  catalog: CarpeDiemCatalogVirtue[];
  accessToken: string | null;
  clientId: string;
  onClose: () => void;
  onSaved: (updated: TrackingItem) => void;
  t: ReturnType<typeof useTranslation>['t'];
}

function ActivityEditDialog({
  item,
  catalog,
  accessToken,
  clientId,
  onClose,
  onSaved,
  t,
}: ActivityEditDialogProps): JSX.Element {
  const lockedAt3 = (item.weight ?? 0) === 3; // 3× is coach-set — read-only for the client.
  const [weight, setWeight] = useState<number>(
    item.weight && item.weight >= 1 ? item.weight : 1,
  );
  const [category, setCategory] = useState<TrackingItem['category']>(item.category);
  const [virtueIds, setVirtueIds] = useState<string[]>(
    item.virtues.map((v) => v.id),
  );
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const catalogByAxis = useMemo(() => {
    const m: Record<Axis, CarpeDiemCatalogVirtue[]> = {
      energy: [],
      work: [],
      love: [],
    };
    for (const v of catalog) m[v.domain].push(v);
    return m;
  }, [catalog]);

  const save = async (): Promise<void> => {
    setSaving(true);
    setErr(null);
    try {
      const updated = await updateTrackingItem(accessToken ?? '', clientId, item.id, {
        weight: lockedAt3 ? 3 : weight,
        category,
      });
      await setTrackingItemVirtues(accessToken ?? '', clientId, item.id, virtueIds);
      const selected = catalog
        .filter((v) => virtueIds.includes(v.id))
        .map((v) => ({ id: v.id, name: v.name, axis: v.domain }));
      onSaved({ ...updated, virtues: selected });
    } catch (cause) {
      setErr(
        cause instanceof ApiError ? cause.envelope.message : t('carpeDiem.errors.generic'),
      );
      setSaving(false);
    }
  };

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="cd-edit-title"
      className="fixed inset-0 z-50 flex items-center justify-center bg-ink/40 px-s4"
      data-testid="carpe-diem-edit-dialog"
    >
      <article className="bg-bone rounded-lg shadow-xl border border-bone-3 max-w-lg w-full p-s6 max-h-[85vh] overflow-y-auto">
        <h2 id="cd-edit-title" className="font-display text-display-md text-ink">
          {item.label}
        </h2>

        {/* Multiplier */}
        <fieldset className="mt-s4">
          <legend className="form-label">{t('carpeDiem.pool.fields.weight')}</legend>
          <div className="mt-s2 inline-flex rounded-md border border-bone-3 bg-bone-2 p-1">
            {[1, 2, 3].map((w) => {
              const isThree = w === 3;
              const active = (lockedAt3 ? 3 : weight) === w;
              return (
                <button
                  key={w}
                  type="button"
                  disabled={isThree && !lockedAt3}
                  onClick={() => {
                    if (!isThree) setWeight(w);
                  }}
                  className={`px-s3 py-1 rounded-sm text-sm font-medium transition-colors ${
                    active
                      ? 'bg-flame text-bone-1'
                      : isThree
                        ? 'text-gray-1 cursor-not-allowed'
                        : 'text-gray-2 hover:text-ink'
                  }`}
                  data-testid={`carpe-diem-edit-weight-${w}`}
                >
                  {w}×
                </button>
              );
            })}
          </div>
          <p className="mt-s1 text-xs text-gray-1">
            {lockedAt3
              ? t('checkIn.tracker.weight.coachLocked')
              : t('checkIn.tracker.weight.hint')}
          </p>
        </fieldset>

        {/* Category (bookend slot) */}
        <fieldset className="mt-s4">
          <legend className="form-label">{t('carpeDiem.pool.fields.category')}</legend>
          <div className="mt-s2 flex flex-wrap gap-1 rounded-md border border-bone-3 bg-bone-2 p-1">
            {POOL_CATEGORIES.map(({ key, labelKey }) => (
              <button
                key={key}
                type="button"
                onClick={() => setCategory(key)}
                className={`px-s3 py-1 rounded-sm text-sm font-medium transition-colors ${
                  category === key ? 'bg-flame text-bone-1' : 'text-gray-2 hover:text-ink'
                }`}
                data-testid={`carpe-diem-edit-category-${key}`}
              >
                {t(labelKey)}
              </button>
            ))}
          </div>
        </fieldset>

        {/* Virtue — axis-grouped (Energy → Work → Love), multi-select */}
        <fieldset className="mt-s4">
          <legend className="form-label">{t('carpeDiem.pool.fields.virtue')}</legend>
          <div className="mt-s2 space-y-s2">
            {AXES.map((ax) => {
              const vs = catalogByAxis[ax];
              if (vs.length === 0) return null;
              return (
                <div key={ax} className="flex flex-wrap items-center gap-1">
                  <AxisIcon axis={ax} size={13} />
                  <span className="text-xs font-mono uppercase tracking-wide text-gray-2 mr-s2">
                    {t(`common.axis.${ax}`)}
                  </span>
                  {vs.map((v) => {
                    const sel = virtueIds.includes(v.id);
                    return (
                      <button
                        key={v.id}
                        type="button"
                        onClick={() =>
                          setVirtueIds((prev) =>
                            sel ? prev.filter((id) => id !== v.id) : [...prev, v.id],
                          )
                        }
                        className={`rounded-full px-s2 py-[2px] text-[11px] font-medium border transition-colors ${
                          sel
                            ? 'bg-flame text-bone-1 border-flame-deep'
                            : 'bg-bone-2 text-gray-2 border-bone-3 hover:border-flame-deep/40'
                        }`}
                        data-testid={`carpe-diem-edit-virtue-${v.id}`}
                      >
                        {v.name}
                      </button>
                    );
                  })}
                </div>
              );
            })}
          </div>
        </fieldset>

        {err && (
          <p className="mt-s3 text-sm text-flame-ember" role="alert">
            {err}
          </p>
        )}

        <div className="mt-s5 flex justify-end gap-s3">
          <button
            type="button"
            onClick={onClose}
            className="btn-ghost text-sm"
            disabled={saving}
          >
            {t('common.actions.cancel')}
          </button>
          <button
            type="button"
            onClick={() => void save()}
            className="btn-primary text-sm"
            disabled={saving}
            data-testid="carpe-diem-edit-save"
          >
            {saving ? t('carpeDiem.affirmation.saving') : t('carpeDiem.pool.save')}
          </button>
        </div>
      </article>
    </div>
  );
}

